import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type CardDetail, useUpdateCardTitle } from '@/entities/card';
import { FormError } from '@/shared/forms';

import { useCardAbilities, useEditingSignal } from './abilities';

import styles from './CardDialog.module.css';

/**
 * The card title. At rest it is a plain heading (the whole title, any number of lines); click or
 * Enter/Space opens a multi-line field. Enter / blur save, Escape reverts.
 *
 * The field exists only while editing, on purpose: sizing it to its content needs a laid-out
 * element, and a field mounted together with the (not yet open) native <dialog> measured 0px
 * and collapsed to a hairline.
 */
export function TitleEditor({ card }: { card: CardDetail }) {
  const { t } = useTranslation();
  const update = useUpdateCardTitle(card.id);
  const { editContent } = useCardAbilities();
  const signal = useEditingSignal();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(card.title);
  const field = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    signal(`title-${card.id}`, editing);
    return () => {
      signal(`title-${card.id}`, false);
    };
  }, [editing, card.id, signal]);

  // Fit the height to the content. Never trust a 0 measurement (element not laid out).
  useLayoutEffect(() => {
    const element = field.current;
    if (!editing || !element) return;
    element.style.height = 'auto';
    if (element.scrollHeight > 0) element.style.height = `${element.scrollHeight}px`;
  }, [value, editing]);

  const finish = (save: boolean) => {
    setEditing(false);
    const title = value.trim();
    if (save && title && title !== card.title) update.mutate(title);
  };

  if (!editContent) {
    return (
      <div className={styles.titleBlock}>
        <h3 className={styles.titleHeading}>
          <span className={styles.titleView}>{card.title}</span>
        </h3>
      </div>
    );
  }
  if (!editing) {
    return (
      <div className={styles.titleBlock}>
        <h3 className={styles.titleHeading}>
          <button
            type="button"
            className={styles.titleView}
            aria-label={`${t('cards.title')}: ${card.title}`}
            onClick={() => {
              setValue(update.isPending && update.variables ? update.variables : card.title);
              setEditing(true);
            }}
          >
            {update.isPending && update.variables ? update.variables : card.title}
          </button>
        </h3>
        <FormError error={update.error} />
      </div>
    );
  }
  return (
    <div className={styles.titleBlock}>
      <label className="visually-hidden" htmlFor={`card-title-${card.id}`}>
        {t('cards.title')}
      </label>
      <textarea
        ref={field}
        id={`card-title-${card.id}`}
        className={styles.titleInput}
        rows={1}
        value={value}
        maxLength={500}
        autoFocus
        onFocus={(e) => {
          e.currentTarget.setSelectionRange(value.length, value.length);
        }}
        onChange={(e) => {
          setValue(e.target.value);
        }}
        onBlur={() => {
          finish(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault(); // a title is one paragraph: Enter saves, it never adds a line
            finish(true);
          } else if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation(); // revert the field instead of closing the dialog
            finish(false);
          }
        }}
      />
    </div>
  );
}
